import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AppContextProvider, useAppContext } from 'src/contexts/AppContext';
import { SiteContentProvider } from 'src/contexts/ContentContext';
import { ScreenSizeProvider } from 'src/contexts/ScreenSizeContext/ScreenSizeContext';
import {
  SiteThemeContextProvider,
  useSiteTheme,
} from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { ThemeUtils } from 'src/core/themes';
import { clearIDBCache } from 'src/core/utils/createIndexDBPersister';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  ClientLogClient,
  ClientLogRequest,
  LogLevel,
} from 'src/WebApiController';
import { DefaultTheme, ThemeProvider } from 'styled-components/macro';

import { SomethingWentWrong } from './SomethingWentWrong';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error) {
      if (ApiException.isApiException(error) && error.status === 401) {
        clearIDBCache(); // delete the cache storage before reloading
        window.location.reload();
      }
    },
  }),
});

const RootErrorPageContent = () => {
  const error = useRouteError();

  const message = (error as { message?: string }).message;
  const status = isRouteErrorResponse(error) ? error.status : undefined;
  const statusText = isRouteErrorResponse(error) ? error.statusText : undefined;
  const stack = (error as { stack?: string }).stack;

  const { activeAccountWebClientConfig, cobrandId } = useAppContext();
  const logClient = useMemo(
    () => new ClientLogClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  useEffect(() => {
    const logRequest: ClientLogRequest = {
      logLevel: LogLevel.Error,
      applicationSource: REACT_APP_NAME,
      errorId: null,
      error: message ?? '',
      pageUrl: window.location.href,
      componentStack: stack ?? null,
      timestamp: new Date().toISOString(),
      additionalInfo: {
        status: status,
        statusText: statusText,
      },
    };

    tryInvokeApi(
      async () => {
        await logClient.logEvents([logRequest]);

        if (message?.includes('Minified React error #301')) {
          // When this happens - the cache is corrupted and usually clearing the cache fixes it
          // So instead of forcing the user to do it automatically - we should just do it and take
          // them back to where they were before
          clearIDBCache();
          window.location.reload();
        }
      },
      () => {
        console.warn('Failed to log error to server');
      }
    );
  }, [logClient, message, status, statusText, stack]);

  const { headerDisplay, messageDisplay } = status
    ? getErrorInfoFromStatusCode(status, statusText)
    : { headerDisplay: undefined, messageDisplay: undefined };

  const { isDarkMode } = useSiteTheme();
  const [legacyTheme, setLegacyTheme] = useState<DefaultTheme>();
  const [isThemeLoading, setIsThemeLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      setIsThemeLoading(true);
      try {
        await ThemeUtils.adjustBodyThemeClasses(cobrandId, isDarkMode);
        const theme = await ThemeUtils.getLegacyThemeForCoBrand(
          cobrandId,
          isDarkMode
        );
        setLegacyTheme(theme);
      } catch (error) {
        console.error('Failed to load theme:', error);
        // Set a fallback theme or handle error
      } finally {
        setIsThemeLoading(false);
      }
    };

    loadTheme();
  }, [cobrandId, isDarkMode]);

  if (isThemeLoading) {
    return <PosSpinner />;
  }

  return (
    <ThemeProvider theme={legacyTheme!}>
      <SomethingWentWrong
        header={headerDisplay}
        message={messageDisplay}
        messages={[message]}
      />
    </ThemeProvider>
  );
};

export const RootErrorPage = (props: FallbackProps) => {
  return (
    <AppContextProvider>
      <QueryClientProvider client={queryClient}>
        <ScreenSizeProvider>
          <SiteContentProvider>
            <SiteThemeContextProvider>
              <RootErrorPageContent />
            </SiteThemeContextProvider>
          </SiteContentProvider>
        </ScreenSizeProvider>
      </QueryClientProvider>
    </AppContextProvider>
  );
};
